import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, fmtIST, fmtISTTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { TestBadge, ApptBadge, EmptyState, BookingStateBadge } from "@/components/common";
import { ExternalServicesWidget } from "@/components/ExternalServicesWidget";
import { IncludeTestToggle } from "@/components/IncludeTestToggle";
import { useIncludeTest, withIncludeTest } from "@/hooks/useIncludeTest";
import { CalendarClock, CheckSquare, Inbox, PhoneMissed, AlertTriangle, ArrowRight } from "lucide-react";

export default function HomePage() {
  const { user, hasCap } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [roleStats, setRoleStats] = useState(null);

  const role = user?.role;
  // Round 7 Workstream F - Home no longer hard-codes include_test=true: TEST
  // is excluded unless an authorised admin turns the (visible) toggle on.
  const { includeTest, canInclude, setIncludeTest } = useIncludeTest();
  const loadHome = useCallback(() => {
    api.get(withIncludeTest("/dashboards/home", includeTest))
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [includeTest]);
  useEffect(() => { loadHome(); }, [loadHome]);
  // 2026-09 P4 (Problem 11) — refetch on regaining focus/visibility, so
  // returning to an already-open Home tab after completing a task
  // elsewhere never shows a stale count indefinitely. Not real-time
  // cross-tab sync (not required) — just "focusing the page refetches".
  useEffect(() => {
    const onFocus = () => loadHome();
    const onVisible = () => { if (document.visibilityState === "visible") loadHome(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [loadHome]);

  useEffect(() => {
    // Role-specific operational blocks
    const jobs = [];
    if (["process", "admin", "manager", "supervisor"].includes(role)) {
      jobs.push(api.get(withIncludeTest("/cases?queue=due_today", includeTest)).then((r) => ["cases_due", r.data.length]).catch(() => null));
      jobs.push(api.get(withIncludeTest("/cases?queue=overdue", includeTest)).then((r) => ["cases_overdue", r.data.length]).catch(() => null));
      jobs.push(api.get("/documents?contact_id=").then(() => null).catch(() => null));
    }
    if (["admin", "manager", "supervisor"].includes(role)) {
      jobs.push(api.get(withIncludeTest("/tickets?status=escalated", includeTest)).then((r) => ["escalations", r.data.length]).catch(() => null));
    }
    if (["counsellor", "admin", "manager", "supervisor"].includes(role)) {
      jobs.push(api.get("/calls/queues?queue=followups_today").then((r) => ["calls_due", r.data.length]).catch(() => null));
    }
    Promise.all(jobs).then((entries) => setRoleStats(Object.fromEntries(entries.filter(Boolean))));
  }, [role, includeTest]);

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <div className="grid md:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => <Skeleton key={i} className="h-40" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="home-dashboard">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">
            Hello, {user?.name?.split(" ")[0]}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Your {role === "process" ? "service delivery" : role === "admin" ? "leadership" : "action"} view for today — {fmtIST(new Date().toISOString(), { year: true, time: false })}</p>
        </div>
        <IncludeTestToggle includeTest={includeTest} canInclude={canInclude} onChange={setIncludeTest} testId="home-include-test" />
      </div>

      {hasCap("admin.config") && <ExternalServicesWidget />}

      {roleStats && Object.keys(roleStats).length > 0 && (
        <div className="flex gap-2 flex-wrap" data-testid="home-role-stats">
          {roleStats.calls_due !== undefined && (
            <Link to="/calls" data-testid="home-calls-due"><Badge variant="outline" className="text-xs py-1.5 px-3">Calls due today: <b className="ml-1">{roleStats.calls_due}</b></Badge></Link>
          )}
          {roleStats.cases_due !== undefined && (
            <Link to="/process?queue=due_today" data-testid="home-cases-due"><Badge variant="outline" className="text-xs py-1.5 px-3">Cases due today: <b className="ml-1">{roleStats.cases_due}</b></Badge></Link>
          )}
          {roleStats.cases_overdue !== undefined && (
            <Link to="/process?queue=overdue" data-testid="home-cases-overdue"><Badge variant="outline" className="text-xs py-1.5 px-3 text-[hsl(var(--danger))]">Cases overdue: <b className="ml-1">{roleStats.cases_overdue}</b></Badge></Link>
          )}
          {roleStats.escalations !== undefined && (
            <Link to="/support" data-testid="home-escalations"><Badge variant="outline" className="text-xs py-1.5 px-3 text-[hsl(var(--danger))]">Escalations: <b className="ml-1">{roleStats.escalations}</b></Badge></Link>
          )}
          {["admin", "manager"].includes(role) && (
            <Link to="/reports" data-testid="home-reports-link"><Badge className="text-xs py-1.5 px-3">Company KPIs →</Badge></Link>
          )}
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Link to="/leads" data-testid="home-pool-card">
          <Card className="hover:border-[hsl(var(--ring))]/50 transition-colors">
            <CardContent className="pt-5 flex items-center gap-3">
              <div className="h-9 w-9 rounded-md bg-[hsl(var(--accent))] flex items-center justify-center">
                <Inbox className="h-4 w-4 text-[hsl(var(--accent-foreground))]" />
              </div>
              <div>
                <div className="text-2xl font-semibold font-heading">{data.lead_pool_count}</div>
                <div className="text-xs text-muted-foreground" title="Active opportunities with no owner (the Lead Pool). Unassigned Cases and unassigned tasks are separate numbers.">Unassigned leads (lead pool)</div>
              </div>
            </CardContent>
          </Card>
        </Link>
        <Link to="/consultations?queue=unassigned" data-testid="home-unassigned-card">
          <Card className="hover:border-[hsl(var(--ring))]/50 transition-colors">
            <CardContent className="pt-5 flex items-center gap-3">
              <div className="h-9 w-9 rounded-md bg-[hsl(var(--warning))]/15 flex items-center justify-center">
                <CalendarClock className="h-4 w-4 text-[hsl(var(--warning))]" />
              </div>
              <div>
                <div className="text-2xl font-semibold font-heading">{data.unassigned_appointments}</div>
                <div className="text-xs text-muted-foreground">Unassigned consultations</div>
              </div>
            </CardContent>
          </Card>
        </Link>
        <Link to="/tasks" data-testid="home-overdue-card">
          <Card className="hover:border-[hsl(var(--ring))]/50 transition-colors">
            <CardContent className="pt-5 flex items-center gap-3">
              <div className="h-9 w-9 rounded-md bg-[hsl(var(--danger))]/10 flex items-center justify-center">
                <AlertTriangle className="h-4 w-4 text-[hsl(var(--danger))]" />
              </div>
              <div>
                <div className="text-2xl font-semibold font-heading" data-testid="home-overdue-total">{data.my_overdue_tasks_total}</div>
                <div className="text-xs text-muted-foreground">My overdue tasks</div>
              </div>
            </CardContent>
          </Card>
        </Link>
        <Link to="/consultations?queue=today" data-testid="home-today-card">
          <Card className="hover:border-[hsl(var(--ring))]/50 transition-colors">
            <CardContent className="pt-5 flex items-center gap-3">
              <div className="h-9 w-9 rounded-md bg-[hsl(var(--success))]/15 flex items-center justify-center">
                <CheckSquare className="h-4 w-4 text-[hsl(var(--success))]" />
              </div>
              <div>
                <div className="text-2xl font-semibold font-heading">{data.todays_consultations.length}</div>
                <div className="text-xs text-muted-foreground">Consultations today</div>
              </div>
            </CardContent>
          </Card>
        </Link>
      </div>

      <div className="grid lg:grid-cols-2 gap-4 min-w-0">
        {hasCap("booking.assign") && data.no_consultant_cancellations.length > 0 && (
          <Card className="min-w-0 lg:col-span-2 border-[hsl(var(--danger))]/40" data-testid="home-no-consultant-cancellations">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-heading flex items-center gap-2 text-[hsl(var(--danger))]">
                <AlertTriangle className="h-4 w-4" />
                Cancelled — no consultant was assigned
                <Badge variant="outline" className="text-[10px] border-[hsl(var(--danger))]/40 text-[hsl(var(--danger))]">{data.no_consultant_cancellations.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <p className="text-xs text-muted-foreground -mt-1 mb-1">These consultations were automatically cancelled because nobody was assigned by the 1-hour mark. Rebook the customer to clear these.</p>
              {data.no_consultant_cancellations.map((o) => (
                <Link key={o.opportunity_id} to={`/customers/${o.contact_id}`} className="flex items-center gap-3 p-2 rounded-md hover:bg-muted transition-colors">
                  <span className="font-medium text-sm flex-1 truncate">{o.contact_name}</span>
                  <TestBadge show={o.is_test_record} />
                  <span className="text-xs text-muted-foreground">{o.product_name}</span>
                  <BookingStateBadge state={o.booking_state} />
                </Link>
              ))}
            </CardContent>
          </Card>
        )}
        <Card className="min-w-0" data-testid="home-todays-consultations">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-heading">Today's consultations</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {data.todays_consultations.length === 0 ? (
              <EmptyState icon={CalendarClock} title="No consultations scheduled today" hint="Booked consultations for today will appear here." />
            ) : data.todays_consultations.map((a) => (
              <Link key={a.appointment_id} to={`/customers/${a.contact_id}`} className="flex items-center gap-3 p-2 rounded-md hover:bg-muted transition-colors">
                <span className="font-mono text-sm w-20">{fmtISTTime(a.scheduled_start)}</span>
                <span className="font-medium text-sm flex-1 truncate">{a.contact_name}</span>
                <TestBadge show={a.is_test_record} />
                <span className="text-xs text-muted-foreground hidden sm:inline">{a.counsellor_name || "Unassigned"}</span>
                <ApptBadge status={a.status} />
              </Link>
            ))}
          </CardContent>
        </Card>

        <Card className="min-w-0" data-testid="home-followups">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              Booking follow-ups needed
              <Badge variant="outline" className="text-[10px]">{data.booking_followups.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {data.booking_followups.length === 0 ? (
              <EmptyState icon={PhoneMissed} title="No pending booking follow-ups" hint="Customers who don't self-book within the wait window appear here." />
            ) : data.booking_followups.map((o) => (
              <Link key={o.opportunity_id} to={`/customers/${o.contact_id}`} className="flex items-center gap-3 p-2 rounded-md hover:bg-muted transition-colors">
                <span className="font-medium text-sm flex-1 truncate">{o.contact_name}</span>
                <TestBadge show={o.is_test_record} />
                <span className="text-xs text-muted-foreground">{o.product_name}</span>
                <BookingStateBadge state={o.booking_state} />
              </Link>
            ))}
          </CardContent>
        </Card>

        <Card className="min-w-0" data-testid="home-my-tasks">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              My open tasks
              <Badge variant="outline" className="text-[10px]" data-testid="home-open-total">{data.my_open_tasks_total}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {data.my_open_tasks.length === 0 ? (
              <EmptyState icon={CheckSquare} title="No open tasks" hint="Tasks assigned to you will appear here." />
            ) : data.my_open_tasks.map((t) => (
              // 2026-09 P4 — `overdue` is the backend's own canonical
              // computation (task_service.is_task_overdue), never a
              // client-side `new Date(t.due_at) < new Date()` recompute —
              // that exact pattern was the P4 audit's confirmed bug (a
              // null due_at reads as epoch-1970, always "overdue").
              <Link key={t.task_id} to={t.contact_id ? `/customers/${t.contact_id}` : "/tasks"} className="flex items-center gap-3 p-2 rounded-md hover:bg-muted transition-colors">
                <span className="font-medium text-sm flex-1 truncate">{t.title}</span>
                <TestBadge show={t.is_test_record} />
                <span className={t.overdue ? "text-xs text-[hsl(var(--danger))] font-medium" : "text-xs text-muted-foreground"}>
                  {t.overdue ? "Overdue · " : "Due "}{fmtIST(t.effective_due_at || t.due_at, { time: false })}
                </span>
              </Link>
            ))}
            {data.my_open_tasks_total > data.my_open_tasks.length && (
              <p className="text-[11px] text-muted-foreground px-2">
                Showing {data.my_open_tasks.length} of {data.my_open_tasks_total}
              </p>
            )}
            <Button asChild variant="ghost" size="sm" className="w-full text-xs">
              <Link to="/tasks" data-testid="home-all-tasks-link">All tasks <ArrowRight className="h-3 w-3 ml-1" /></Link>
            </Button>
          </CardContent>
        </Card>

        <Card className="min-w-0" data-testid="home-no-shows">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-heading">Recent no-shows</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {data.recent_no_shows.length === 0 ? (
              <EmptyState icon={PhoneMissed} title="No recent no-shows" />
            ) : data.recent_no_shows.map((a) => (
              <Link key={a.appointment_id} to={`/customers/${a.contact_id}`} className="flex items-center gap-3 p-2 rounded-md hover:bg-muted transition-colors">
                <span className="font-medium text-sm flex-1 truncate">{a.contact_name}</span>
                <TestBadge show={a.is_test_record} />
                <span className="text-xs text-muted-foreground">{fmtIST(a.scheduled_start)}</span>
                <ApptBadge status={a.status} />
              </Link>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
